import { Injectable } from '@angular/core';
import { ExtractionInfo, System, SystemDetails } from '@frontend-site/shared/data';
import { Repository } from '@frontend-site/shared/utils';
import { createStore, select, withProps } from '@ngneat/elf';
import { SystemState } from './system-state';
import { Observable, combineLatest, map } from 'rxjs';

function createInitialState(): SystemState {
    return {
        systems: [],
        systemsDetails: [],
        systemsExtractionInfo: []
    };
}

@Injectable({ providedIn: 'root' })
export class SystemStore extends Repository<SystemState> {
    readonly systems$ = this.store.pipe(select((x) => x.systems));
    readonly selectedSystemId$ = this.store.pipe(select((x) => x.selectedSystemId));
    readonly systemsDetails$ = this.store.pipe(select((x) => x.systemsDetails));
    readonly companyId$ = this.systemsDetails$.pipe(select((m) => m?.pop()?.companyId));
    readonly systemsExtractionInfo$ = this.store.pipe(select((x) => x.systemsExtractionInfo));

    get systemId(): number {
        return this.getValue().selectedSystemId as number;
    }

    get systemDetails$(): Observable<SystemDetails | null | undefined> {
        return combineLatest([this.selectedSystemId$, this.systemsDetails$]).pipe(
            map(([selectedSystemId, systemsDetails]) => systemsDetails.find((s) => s.systemId === selectedSystemId))
        );
    }

    get systemDetails(): SystemDetails | null | undefined {
        return this.getValue().systemsDetails.filter((s) => s.systemId === this.systemId)[0];
    }

    get extractionInfo$(): Observable<ExtractionInfo | null | undefined> {
        return combineLatest([this.selectedSystemId$, this.systemsExtractionInfo$]).pipe(
            map(([selectedSystemId, systemsExtractionInfo]) => systemsExtractionInfo.find((s) => s.systemId === selectedSystemId))
        );
    }

    get extractionInfo(): ExtractionInfo {
        return this.getValue().systemsExtractionInfo[this.systemId];
    }

    constructor() {
        super(createStore({ name: 'SystemsStore' }, withProps<SystemState>(createInitialState())));
    }

    setSystems(systems: System[]): void {
        this.update({ systems });
    }

    setExtractionInfo(systemId: number, extractionInfo: ExtractionInfo): void {
        this.store.update((state) => {
            const systemsExtractionInfo = state.systemsExtractionInfo.map((s) => {
                if (s.systemId === systemId) {
                    return extractionInfo;
                }
                return s;
            });
            if (!systemsExtractionInfo.includes(extractionInfo)) {
                systemsExtractionInfo.push(extractionInfo);
            }
            return { ...state, systemsExtractionInfo };
        });
    }

    updateSystemName(systemId: number, systemName: string): void {
        this.store.update((state) => {
            const systems = state.systems.map((s) => {
                if (s.id === systemId) {
                    return { ...s, name: systemName };
                }
                return s;
            });
            const systemsDetails = state.systemsDetails?.map((currentSystemDetails) => {
                if (currentSystemDetails.systemId === systemId) {
                    return { ...currentSystemDetails, systemName };
                }
                return currentSystemDetails;
            });

            return { ...state, systems, systemsDetails };
        });
    }

    updateSystemDetails(systemId: number, systemDetails: SystemDetails | null): void {
        let sysDetails: SystemDetails[];
        this.store.update((state: SystemState) => {
            if (systemDetails && !state.systemsDetails?.find((currentSystemDetails) => currentSystemDetails.systemId === systemId)) {
                sysDetails = [...state.systemsDetails, systemDetails];
            } else {
                sysDetails = state.systemsDetails?.map((currentSystemDetails) =>
                    currentSystemDetails.systemId === systemId ? { ...currentSystemDetails, ...systemDetails } : currentSystemDetails
                );
            }
            return { ...state, systemsDetails: sysDetails };
        });
    }

    clearSelectedSystemId(): void {
        this.update({ selectedSystemId: null });
    }
}
